import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { IcoBolt, IcoCheck, IcoSparkle, IcoX } from "@/components/icons";

export type ToastType = "ok" | "err" | "warn" | "info";

interface ToastItem {
  id: number;
  msg: string;
  type: ToastType;
}

declare global {
  interface Window {
    __toast?: (msg: string, type?: ToastType, duration?: number) => void;
  }
}

// `tint` layers a translucent type color over the opaque panel via
// background-image (over background-color), so both render -- two Tailwind
// `bg-*` utilities would collide and only one would win.
const tintStyle = (rgba: string): CSSProperties => ({
  // `--ef-panel` n'existe pas: styles.css definit `--panel`. Le fond etait
  // donc invalide et TOUS les retours de l'application -- creations,
  // suppressions, erreurs de paiement, exports -- s'affichaient translucides
  // par-dessus le contenu, avec pour seul fond leur teinte a 12 %.
  backgroundColor: "var(--panel)",
  backgroundImage: `linear-gradient(${rgba}, ${rgba})`,
});
const STYLE: Record<ToastType, { border: string; tint: CSSProperties; text: string; icon: ReactNode }> = {
  ok: { border: "border-ef-ok/30", tint: tintStyle("rgba(34,197,94,0.12)"), text: "text-ef-ok", icon: <IcoCheck size={14} /> },
  err: { border: "border-ef-err/30", tint: tintStyle("rgba(242,48,48,0.12)"), text: "text-ef-err", icon: <IcoX size={14} /> },
  warn: { border: "border-ef-warn/30", tint: tintStyle("rgba(245,165,36,0.12)"), text: "text-ef-warn", icon: <IcoBolt size={14} /> },
  info: { border: "border-ef-accent/25", tint: tintStyle("rgba(34,197,176,0.10)"), text: "text-ef-accent", icon: <IcoSparkle size={14} /> },
};

let _seq = 1;

// Mount once at app root. Exposes window.__toast(msg, type, duration).
export function ToastSystem() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    window.__toast = (msg, type = "info", duration = 3200) => {
      const id = _seq++;
      setItems((prev) => [...prev, { id, msg, type }]);
      window.setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), duration);
    };
    return () => {
      delete window.__toast;
    };
  }, []);

  if (items.length === 0) return null;

  return (
    // `role=status` + `aria-live`: ces messages apparaissent seuls, un lecteur
    // d'ecran ne les annoncait pas. `bottom-20` sous `md`: la barre de
    // navigation basse fait 56 px et les recouvrait sur telephone.
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-20 right-4 z-[10000] flex flex-col gap-2 md:bottom-6 md:right-6"
    >
      {items.map((t) => {
        const s = STYLE[t.type];
        return (
          <div
            key={t.id}
            style={s.tint}
            className={`pointer-events-auto flex items-center gap-2.5 rounded-[var(--radius)] border ${s.border} px-3.5 py-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.4)] [animation:slideUp_0.2s_ease]`}
          >
            <span className={s.text}>{s.icon}</span>
            <span className="max-w-[280px] text-[13px] text-ef-text">{t.msg}</span>
          </div>
        );
      })}
    </div>
  );
}
